// @ts-nocheck
/* Port of ReleaseFormModal.js (492L). BYTE-IDENTICAL.
 * 3-step wizard: Basics -> What changed -> Full notes. `backdrop="static"`
 * blocks accidental dismiss while saving. File-import replaces
 * content_html and also prefills title from filename when empty.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { createRelease, updateRelease } from './releaseCenterService';
import { FiX, FiUploadCloud, FiPlus } from 'react-icons/fi';
import { RELEASE_TYPE_META, HIGHLIGHT_CATEGORY_META } from './releaseUtils';
import { extractContentFromFile } from './releaseImport';
import CkEditorField from './CkEditorField';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const RELEASE_TYPES = Object.keys(RELEASE_TYPE_META);
const CATEGORIES = Object.keys(HIGHLIGHT_CATEGORY_META);

const STEP_LABELS = ['Basics', 'What changed', 'Full notes'];

const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const stateClass = (value: any, base = 'rc-input') =>
  `${base} ${String(value ?? '').trim() ? 'rc-ok' : 'rc-empty'}`;

const emptyForm: any = {
  version: '',
  title: '',
  release_type: 'Minor',
  release_date: '',
  summary: '',
  quick_summary: '',
  content_html: '',
  is_published: true,
  is_pinned: false,
};

const ReleaseFormModal = ({
  show,
  onHide,
  release,
  tags = [],
  onSaved,
}: any) => {
  const isEdit = Boolean(release?.id);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<any>(emptyForm);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [highlights, setHighlights] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const fileRef = useRef<any>(null);

  useEffect(() => {
    if (!show) return;
    setStep(0);
    if (isEdit) {
      setForm({
        version: release.version || '',
        title: release.title || '',
        release_type: release.release_type || 'Minor',
        release_date: (release.release_date || '').slice(0, 10),
        summary: release.summary || '',
        quick_summary: release.quick_summary || '',
        content_html: release.content_html || '',
        is_published: !!release.is_published,
        is_pinned: !!release.is_pinned,
      });
      setSelectedTags((release.tags || []).map((t: any) => t.id));
      setHighlights(
        (release.highlights || [])
          .slice()
          .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((h: any) => ({ category: h.category, label: h.label })),
      );
    } else {
      setForm(emptyForm);
      setSelectedTags([]);
      setHighlights([{ category: 'Added', label: '' }]);
    }
    setErrors({});
  }, [show, isEdit, release]);

  const setField = (key: string, value: any) =>
    setForm((f: any) => ({ ...f, [key]: value }));

  const toggleTag = (id: any) =>
    setSelectedTags((prev: any[]) =>
      prev.includes(id) ? prev.filter((x: any) => x !== id) : [...prev, id],
    );

  const addHighlight = () =>
    setHighlights((h: any[]) => [...h, { category: 'Added', label: '' }]);
  const updateHighlight = (i: number, key: string, value: any) =>
    setHighlights((h: any[]) =>
      h.map((row: any, idx: number) =>
        idx === i ? { ...row, [key]: value } : row,
      ),
    );
  const removeHighlight = (i: number) =>
    setHighlights((h: any[]) =>
      h.filter((_: any, idx: number) => idx !== i),
    );

  const validateBasics = () => {
    const e: any = {};
    if (!form.version.trim()) e.version = 'Version is required';
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.release_date) e.release_date = 'Release date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 0 && !validateBasics()) return;
    setStep((s: number) => Math.min(s + 1, STEP_LABELS.length - 1));
  };
  const back = () => setStep((s: number) => Math.max(s - 1, 0));

  const onPickFile = async (e: any) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const { html, kind }: any = await extractContentFromFile(file);
      if (!html || !html.trim()) {
        toast.warning("Couldn't extract any content from that file.");
        return;
      }
      setField('content_html', html);
      if (!form.title.trim()) {
        const base = file.name
          .replace(/\.(pdf|docx?)$/i, '')
          .replace(/[_-]+/g, ' ');
        setField('title', base.trim());
      }
      toast.success(
        kind === 'pdf'
          ? "PDF text imported. Images from PDFs can't be auto-extracted — add them in the editor."
          : 'Word document imported with images & links.',
      );
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const submit = async () => {
    if (!validateBasics()) {
      setStep(0);
      return;
    }
    setSaving(true);
    const payload: any = {
      ...form,
      version: form.version.trim(),
      title: form.title.trim(),
      tag_ids: selectedTags,
      highlights: highlights
        .filter((h: any) => h.label.trim())
        .map((h: any, idx: number) => ({
          category: h.category,
          label: h.label.trim(),
          sort_order: idx,
        })),
    };
    try {
      if (isEdit) {
        await updateRelease(release.id, payload);
        toast.success('Release updated');
      } else {
        await createRelease(payload);
        toast.success('Release created');
      }
      onSaved?.();
      onHide();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const tagOptions = useMemo(() => tags, [tags]);
  const isLast = step === STEP_LABELS.length - 1;

  return (
    <Dialog
      open={!!show}
      onOpenChange={(o: boolean) => {
        if (!o && !saving) onHide?.();
      }}
    >
      <DialogContent
        className="sm:max-w-2xl rc-modal p-0 gap-0 overflow-hidden border-0"
        aria-labelledby="rc-form-title"
        onInteractOutside={(e: any) => e.preventDefault()}
      >
      <div className="rc-modal__hero">
        {!saving && (
          <button
            type="button"
            className="rc-modal__close"
            onClick={onHide}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        )}
        <h2 className="rc-modal__title" id="rc-form-title">
          {isEdit ? 'Edit release' : 'Create a release'}
        </h2>
        <div className="rc-modal__date">
          Step {step + 1} of {STEP_LABELS.length} — {STEP_LABELS[step]}
        </div>
      </div>

      <div className="rc-modal__body">
        {/* Stepper */}
        <div className="rc-steps">
          {STEP_LABELS.map((label: any, i: number) => (
            <Fragment key={label}>
              <div className="rc-step-pill">
                <span
                  className={`rc-step-dot ${
                    i === step
                      ? 'rc-step-dot--active'
                      : i < step
                      ? 'rc-step-dot--done'
                      : ''
                  }`}
                >
                  {i < step ? '✓' : i + 1}
                </span>
                <span
                  className={`rc-step-label ${
                    i === step ? '' : 'rc-step-label--muted'
                  }`}
                >
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div
                  className={`rc-step-bar ${
                    i < step ? 'rc-step-bar--done' : ''
                  }`}
                />
              )}
            </Fragment>
          ))}
        </div>

        {/* STEP 1 -- Basics */}
        {step === 0 && (
          <>
            <div className="rc-grid-2">
              <div className="rc-field">
                <label>Version *</label>
                <input
                  className={stateClass(form.version)}
                  placeholder="2.4.0"
                  value={form.version}
                  inputMode="decimal"
                  autoComplete="off"
                  pattern="[0-9]+(\.[0-9]+){0,3}"
                  title="Digits and dots only, e.g. 2.4.0"
                  onKeyDown={(e) => {
                    // Allow control/navigation keys; block anything that
                    // isn't a digit or a dot. Prevents alphabets from
                    // being typed into a semver field.
                    const nav = [
                      'Backspace',
                      'Delete',
                      'Tab',
                      'Escape',
                      'Enter',
                      'ArrowLeft',
                      'ArrowRight',
                      'ArrowUp',
                      'ArrowDown',
                      'Home',
                      'End',
                    ];
                    if (e.ctrlKey || e.metaKey || nav.includes(e.key)) return;
                    if (!/^[0-9.]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Belt-and-braces: paste events can still slip
                    // non-numeric chars through onKeyDown, so strip them
                    // out at the state layer too.
                    const clean = e.target.value.replace(/[^0-9.]/g, '');
                    setField('version', clean);
                  }}
                />
                <div className="rc-hint">
                  A unique semantic version, e.g. 2.4.0.
                </div>
                {errors.version && (
                  <small style={{ color: '#dc2626' }}>{errors.version}</small>
                )}
              </div>
              <div className="rc-field">
                <label>Release date *</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <CalendarDateInput
                    value={form.release_date}
                    onChange={(e) => setField('release_date', e)}
                    className={stateClass(form.release_date)}
                  />
                  <button
                    type="button"
                    className="rc-btn rc-btn--ghost"
                    style={{ height: 42, whiteSpace: 'nowrap' }}
                    title="Set to today"
                    onClick={() => setField('release_date', todayISO())}
                  >
                    Today
                  </button>
                </div>
                {errors.release_date && (
                  <small style={{ color: '#dc2626' }}>
                    {errors.release_date}
                  </small>
                )}
              </div>
            </div>

            <div className="rc-field">
              <label>Title *</label>
              <input
                className={stateClass(form.title)}
                placeholder="Faster call routing & smarter analytics"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
              />
              <div className="rc-hint">
                The headline shown on the card and detail view.
              </div>
              {errors.title && (
                <small style={{ color: '#dc2626' }}>{errors.title}</small>
              )}
            </div>

            <div className="rc-grid-2">
              <div className="rc-field">
                <label>Release type</label>
                <select
                  className="rc-select"
                  value={form.release_type}
                  style={{ color: 'black' }}
                  onChange={(e) => setField('release_type', e.target.value)}
                >
                  {RELEASE_TYPES.map((t: any) => (
                    <option key={t} value={t} style={{ color: 'black' }}>
                      {t}
                    </option>
                  ))}
                </select>
                <div className="rc-hint">
                  Major, Minor, Patch, Hotfix or Security.
                </div>
              </div>
              <div className="rc-field">
                <label>Summary</label>
                <input
                  className={stateClass(form.summary)}
                  placeholder="One-line teaser shown on the card"
                  value={form.summary}
                  onChange={(e) => setField('summary', e.target.value)}
                />
                <div className="rc-hint">
                  Optional. Keep it to a single sentence.
                </div>
              </div>
            </div>
          </>
        )}

        {/* STEP 2 -- What changed */}
        {step === 1 && (
          <>
            <div className="rc-field">
              <label>Quick summary</label>
              <input
                className={stateClass(form.quick_summary)}
                placeholder="A scannable intro line for people who don't read"
                value={form.quick_summary}
                onChange={(e) => setField('quick_summary', e.target.value)}
              />
              <div className="rc-hint">
                Shown above the checklist in the detail view.
              </div>
            </div>

            <div className="rc-field">
              <label>Tags</label>
              <div className="rc-tag-picker">
                {tagOptions.map((t: any) => {
                  const on = selectedTags.includes(t.id);
                  return (
                    <button
                      type="button"
                      key={t.id}
                      aria-pressed={on}
                      className={`rc-tag-pick ${on ? 'rc-tag-pick--on' : ''}`}
                      onClick={() => toggleTag(t.id)}
                    >
                      {on && (
                        <span className="rc-tag-pick__check" aria-hidden="true">
                          ✓
                        </span>
                      )}
                      {t.name}
                    </button>
                  );
                })}
                {!tagOptions.length && (
                  <small style={{ color: 'var(--rc-text-muted)' }}>
                    No tags available.
                  </small>
                )}
              </div>
              <div className="rc-hint">
                Pick tags so the filter chips can surface this release.
              </div>
            </div>

            <div className="rc-field">
              <label>What changed (checklist)</label>
              <div className="rc-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                Add short, scannable items. The category powers the
                side-by-side Compare view.
              </div>
              {highlights.map((h: any, i: number) => (
                <div className="rc-hl-row" key={i}>
                  <select
                    className="rc-select"
                    value={h.category}
                    style={{ color: 'black' }}
                    onChange={(e) =>
                      updateHighlight(i, 'category', e.target.value)
                    }
                  >
                    {CATEGORIES.map((c: any) => (
                      <option key={c} value={c} style={{ color: 'black' }}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input
                    className={stateClass(h.label)}
                    placeholder="e.g. Faster Call Routing"
                    value={h.label}
                    onChange={(e) =>
                      updateHighlight(i, 'label', e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="rc-icon-btn"
                    onClick={() => removeHighlight(i)}
                    aria-label="Remove highlight"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="rc-btn rc-btn--ghost"
                onClick={addHighlight}
              >
                <FiPlus style={{ marginRight: 6 }} /> Add highlight
              </button>
            </div>
          </>
        )}

        {/* STEP 3 -- Full notes & publish */}
        {step === 2 && (
          <>
            <div className="rc-import">
              <span className="rc-import__ico" aria-hidden="true">
                <FiUploadCloud size={20} />
              </span>
              <div className="rc-import__text">
                <b>Import from PDF or Word</b>
                <span>
                  Auto-fills the notes below — Word keeps images & links; PDF
                  imports text.
                </span>
              </div>
              <button
                type="button"
                className="rc-btn rc-btn--primary"
                disabled={importing}
                onClick={() => fileRef.current?.click()}
              >
                {importing ? 'Importing…' : 'Choose file'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                style={{ display: 'none' }}
                onChange={onPickFile}
              />
            </div>

            <div className="rc-field">
              <label>Full release notes</label>
              <CkEditorField
                value={form.content_html}
                onChange={(html: any) => setField('content_html', html)}
              />
              <div className="rc-hint">
                Supports images, tables, code blocks, videos, links, headings
                & quotes.
              </div>
            </div>

            <div className="rc-field">
              <label>Publish status</label>
              <div
                style={{ display: 'flex', gap: 20, alignItems: 'center' }}
              >
                <label
                  style={{
                    display: 'flex',
                    gap: 6,
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.is_published}
                    onChange={(e) =>
                      setField('is_published', e.target.checked)
                    }
                  />
                  Published
                </label>
                <label
                  style={{
                    display: 'flex',
                    gap: 6,
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.is_pinned}
                    onChange={(e) => setField('is_pinned', e.target.checked)}
                  />
                  Pin as featured
                </label>
              </div>
              <div className="rc-hint">
                Leave "Published" off to save it as a Draft.
              </div>
            </div>
          </>
        )}

        {/* Footer nav */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 18,
            paddingTop: 16,
            borderTop: '1px dashed var(--rc-border)',
          }}
        >
          <button
            type="button"
            className="rc-btn rc-btn--ghost"
            onClick={step === 0 ? onHide : back}
            disabled={saving}
          >
            {step === 0 ? 'Cancel' : '← Back'}
          </button>

          {isLast ? (
            <button
              type="button"
              className="rc-btn rc-btn--primary"
              onClick={submit}
              disabled={saving}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create release'}
            </button>
          ) : (
            <button
              type="button"
              className="rc-btn rc-btn--primary"
              onClick={next}
            >
              {'Next →'}
            </button>
          )}
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseFormModal;
